import { Injectable } from '@angular/core';
import { environment } from '@home/src/app/environments/environment';
import { IMessage } from '@home/src/app/Interface/messages';
import { BehaviorSubject, map } from 'rxjs';
import { ApiUtils } from '@core/services/Utils/apiUtils';
import {
  get,
  post,
  deleteRequest,
  patch,
} from '@core/interceptors/fetch-client';
import { INotification } from '@home/src/app/Interface/notification';

@Injectable({
  providedIn: 'root',
})
export class CommentsService {
  // BehaviorSubject to manage open/close state for messages
  private isOpen = new BehaviorSubject<boolean>(false);
  isOpen$ = this.isOpen.asObservable();

  // New BehaviorSubject for enabling/disabling add comment
  private enableAddComment = new BehaviorSubject<boolean>(false);
  enableAddComment$ = this.enableAddComment.asObservable();

  private title = new BehaviorSubject<string>('Messages');
  title$ = this.title.asObservable();

  private messages = new BehaviorSubject<IMessage[]>([]);
  messages$ = this.messages.asObservable();

  private groupMembersList = new BehaviorSubject<any[]>([]);
  groupMembersList$ = this.groupMembersList.asObservable();

  private type = new BehaviorSubject<string | null>(null);
  type$ = this.type.asObservable();

  private subject = new BehaviorSubject<string | null>(null);
  subject$ = this.subject.asObservable();

  readonly unreadCount$ = this.messages$.pipe(
    map((messages) => messages.filter((m) => !m.isRead).length),
  );

  // BehaviorSubject to manage open/close state for notifications
  private isNotificationOpen = new BehaviorSubject<boolean>(false);
  isNotificationOpen$ = this.isNotificationOpen.asObservable();

  private serverNotifications = new BehaviorSubject<INotification[]>([]);
  serverNotifications$ = this.serverNotifications.asObservable();

  private localNotifications = new BehaviorSubject<INotification[]>([]);
  localNotifications$ = this.localNotifications.asObservable();

  get unreadNotificationsCount$() {
    const serverCount = this.serverNotifications.value.filter(
      (n) => !n.isRead,
    ).length;
    const localCount = this.localNotifications.value.filter(
      (n) => !n.isRead,
    ).length;
    return serverCount + localCount;
  }

  constructor() {}

  setTitle(value: string) {
    this.title.next(value);
  }
  setMessages(value: IMessage[]) {
    this.messages.next([...value]);
  }
  setGroupMembersList(value: IMessage[]) {
    this.groupMembersList.next([...value]);
  }
  setType(value: string | null) {
    this.type.next(value);
  }
  setSubhect(value: string | null) {
    this.subject.next(value);
  }
  setOpenCommentsPanel(value: boolean) {
    this.isOpen.next(value);
  }
  setEnableAddComment(value: boolean) {
    this.enableAddComment.next(value);
  }
  setNotificationOpen(value: boolean) {
    this.isNotificationOpen.next(value);
  }
  // Server Notifications
  addServerNotification(value: INotification) {
    this.serverNotifications.next([value, ...this.serverNotifications.value]);
  }
  setAllServerNotifications(value: INotification[]) {
    this.serverNotifications.next([...value]);
  }
  async clearAllServerNotifications(payload: any) {
    let response = null;
    try {
      await ApiUtils.handleApiResponse1(
        post<any>(environment.deleteNotification, payload),
        async (data, statusCode) => {
          response = { data, statusCode };
        },
        'Failed to add new comment',
      );
    } catch (error: any) {
      response = {
        data: null,
        statusCode: error.status || error.statusCode || 500,
      };
    }
    return response;
  }
  removeServerNotification(value: INotification, emailAddress: string) {
    return this.clearAllServerNotifications({
      emailAddress,
      notificationIds: [value.notificationId],
    });
  }
  // Local Notiffications
  addLocalNotification(value: INotification) {
    this.localNotifications.next([value, ...this.localNotifications.value]);
  }
  setAllLocalNotifications(value: INotification[]) {
    this.localNotifications.next([...value]);
  }
  clearAllLocalNotifications() {
    this.localNotifications.next([]);
  }
  removeLocalNotification(value: INotification) {
    const filtered = this.localNotifications.value.filter(
      (n) => n.notificationId !== value.notificationId,
    );
    this.localNotifications.next(filtered);
  }

  async getAllMessages(email: string) {
    await ApiUtils.handleApiResponse1(
      get<any>(`${environment.getCommentsByType}?emailAddress=${email}`),
      async (data, statusCode) => {
        if (statusCode === 200) await this.setMessages([...data]);
        else await this.setMessages([]);
      },
      'Failed to fetch messages',
    );
  }

  async getGroupMembersList(email: string) {
    await ApiUtils.handleApiResponse1(
      get<any>(`${environment.getGroupMembersList}?emailAddress=${email}`),
      async (data, statusCode) => {
        if (statusCode === 200) await this.setGroupMembersList([...data]);
        else await this.setGroupMembersList([]);
      },
      'Failed to fetch member list',
    );
  }

  async addNewComment(comment: any) {
    let response = null;
    try {
      await ApiUtils.handleApiResponse1(
        post<any>(environment.addComment, comment),
        async (data, statusCode) => {
          response = { data, statusCode };
        },
        'Failed to add new comment',
      );
    } catch (error: any) {
      response = {
        data: null,
        statusCode: error.status || error.statusCode || 500,
      };
    }
    return response;
  }

  async markMessagesAsRead(commentIds: number[], emailAddress: string) {
    await ApiUtils.handleApiResponse1(
      patch<any>(environment.markCommentsAsRead, { commentIds, emailAddress }),
      () => {},
      'Failed to mark messages as read',
    );
  }

  async deleteComment(commentId: number) {
    let response = null;
    try {
      await ApiUtils.handleApiResponse1(
        deleteRequest<any>(
          `${environment.deleteComment}?commentId=${commentId}`,
        ),
        async (data, statusCode) => {
          response = { data, statusCode };
        },
        'Failed to delete comment',
      );
    } catch (error: any) {
      response = {
        data: null,
        statusCode: error.status || error.statusCode || 500,
      };
    }
    return response;
  }

  async getAllNotifications(email: string) {
    await ApiUtils.handleApiResponse1(
      get<any>(`${environment.getAllNotification}?emailAddress=${email}`),
      async (data, statusCode) => {
        if (statusCode === 200) {
          const updatedData = data.map((notification: INotification) => ({
            ...notification,
            isServer: true, // Marking all fetched notifications as server notifications
          }));
          await this.setAllServerNotifications([...updatedData]);
        } else await this.setAllServerNotifications([]);
      },
      'Failed to fetch Notifications',
    );
  }

  async markServerNotificationsAsRead(
    notificationIds: (number | string)[],
    emailAddress: string,
  ) {
    await ApiUtils.handleApiResponse1(
      patch<any>(environment.markNotificationsAsRead, {
        notificationIds,
        emailAddress,
      }),
      () => {},
      'Failed to mark messages as read',
    );
  }
}
